import {useEffect, useState} from 'react'
import {
    useGetProfileQuery,
    useUpdateProfileMutation,
    useUploadImageMutation,
} from 'store/Store.js'
import {i18n} from 'common.js'
import {DEFAULT_PHONE_COUNTRY, joinPhone, splitPhone} from './phoneCountries.js'

const useProfile = () => {
    const {data: user, isProfileLoading, isSuccess} = useGetProfileQuery()
    const [uploadImage] = useUploadImageMutation()
    const [isImageLoading, setImageLoading] = useState(false)
    const [doSave, {isLoading: isSaveProfile}] = useUpdateProfileMutation()
    const [profileImageUrl, setProfileImageUrl] = useState(null)
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    })
    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY)
    const [phoneError, setPhoneError] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState('success')

    const t18Profile = (text) => i18n.t(`profile.${text}`)

    const handleInputChange = (event) => {
        const {name, value} = event.target
        if (name === 'phone') {
            setPhoneError(false)
        }
        setProfileData((prevData) => ({
            ...prevData,
            [name]: value,
        }))
    }

    const handlePhoneCountryChange = (country) => {
        setPhoneError(false)
        setPhoneCountry(country)
    }

    useEffect(() => {
        if (user) {
            const {country, number} = splitPhone(user.phone)
            setPhoneCountry(country)
            setProfileData({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phone: number,
            })
        }
    }, [user])
    const handleImageChange = async (e) => {
        const file = e.target.files[0]
        const formData = new FormData()
        formData.append('files', file)

        try {
            setImageLoading(true)
            const {data} = await uploadImage({
                body: formData,
            })
            const {fullLink: imageUrl} = data.files[0]
            setProfileImageUrl(imageUrl)
        } catch (e) {
            console.log('err', e)
        }
        setImageLoading(false)
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const phone = joinPhone(phoneCountry, profileData.phone)
        if (phone === null) {
            setPhoneError(true)
            return
        }
        const body = {
            ...profileData,
            phone,
            profileImageUrl: profileImageUrl || user.profileImageUrl,
        }
        try {
            await doSave(body).unwrap()
            setSnackbarSeverity('success')
            setSnackbarMessage(t18Profile('changesSaved'))
        } catch (e) {
            setSnackbarSeverity('error')
            setSnackbarMessage(e?.data?.message ?? 'Something went wrong')
            console.log(e)
        }
        setSnackbarOpen(true)
    }

    const handleSnackbarClose = (reason) => {
        if (reason === 'clickaway') {
            return
        }
        setSnackbarOpen(false)
    }

    return {
        user,
        userNotFound: !isProfileLoading && !isSuccess,
        profileData,
        profileImageUrl,
        isProfileLoading,
        isImageLoading,
        t18Profile,
        handleSubmit,
        isSaveProfile,
        handleInputChange,
        handleImageChange,
        snackbarOpen,
        handleSnackbarClose,
        snackbarMessage,
        snackbarSeverity,
        phoneError,
        phoneCountry,
        handlePhoneCountryChange,
    }
}

export default useProfile
